import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Card, CardActionArea, CardContent,
  Avatar, CircularProgress
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PortalAcademy } from '../../types/academy';
import { useStudentPortalLanguage } from './contexts/StudentPortalLanguageContext';

interface Props {
  open: boolean;
  academies: PortalAcademy[];
  onSelect: (dos_idf: number) => void;
  loading?: boolean;
}

export const StudentAcademySelectModal: React.FC<Props> = ({
  open,
  academies,
  onSelect,
  loading = false,
}) => {
  const { t } = useStudentPortalLanguage();
  const [selectedId, setSelectedId] = useState<number>(() => {
    const first = academies[0] as any;
    return first?.dos_idf ?? first?.dosId ?? 0;
  });

  const handleConfirm = () => {
    if (selectedId) {
      onSelect(selectedId);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'info.main', color: 'white' }}>
            <SchoolIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('portal.student.select_academy_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('portal.student.select_academy_desc')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {academies.map((aca, idx) => {
            const a = aca as any;
            const acaId = a.dos_idf ?? a.dosId ?? idx + 1;
            const acaCode = a.dos_code ?? a.dosCode ?? '';
            const acaLib = a.dos_lib ?? a.dosLib ?? acaCode;
            const acaLogo = a.logo_base64 ?? a.dosLogo ?? a.logo;
            const acaPhone = a.phone ?? a.dosTel ?? '';
            const isSelected = acaId === selectedId;
            return (
              <Card
                key={acaId}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: isSelected ? 'info.main' : 'divider',
                  bgcolor: isSelected ? 'info.50' : 'background.paper',
                  borderWidth: isSelected ? 2 : 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'info.main',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardActionArea onClick={() => setSelectedId(acaId)}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Avatar
                      src={acaLogo}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'info.light',
                        color: 'info.dark',
                        fontWeight: 700
                      }}
                    >
                      {acaCode?.[0] || 'A'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {acaLib || acaCode}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Code: <strong>{acaCode}</strong>
                        {acaPhone ? ` • ${acaPhone}` : ''}
                      </Typography>
                    </Box>
                    {isSelected && (
                      <CheckCircleIcon sx={{ color: 'info.main', fontSize: 28 }} />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="info"
          fullWidth
          size="large"
          disabled={!selectedId || loading}
          onClick={handleConfirm}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('portal.student.btn_access_academy')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
